import { useEffect, useRef } from 'react';

/**
 * Calls `onIdle` after `timeoutMs` of no user activity.
 *
 * Activity = mouse, keyboard, touch, scroll, or wheel events. Instead of
 * resetting a setTimeout on every event (expensive under mousemove), we
 * just stamp `lastActivity` and let a 5-second interval check elapsed time.
 * The timestamp approach also catches laptop sleep / tab suspension: if the
 * machine was asleep past the deadline, the very next tick fires onIdle.
 *
 * `enabled` lets the caller pause detection (e.g. while already locked).
 */
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'wheel'];
const CHECK_INTERVAL_MS = 5000;

export default function useIdleTimer({ timeoutMs, onIdle, enabled = true }) {
  const lastActivity = useRef(Date.now());
  const onIdleRef = useRef(onIdle);
  onIdleRef.current = onIdle;

  useEffect(() => {
    if (!enabled) return undefined;

    lastActivity.current = Date.now();

    const markActivity = () => {
      lastActivity.current = Date.now();
    };

    const checkIdle = () => {
      if (Date.now() - lastActivity.current >= timeoutMs) {
        onIdleRef.current?.();
      }
    };

    // Re-check immediately when the tab becomes visible again, so waking
    // a machine that slept past the deadline locks before content is seen.
    const onVisibility = () => {
      if (document.visibilityState === 'visible') checkIdle();
    };

    ACTIVITY_EVENTS.forEach((ev) =>
      window.addEventListener(ev, markActivity, { passive: true }),
    );
    document.addEventListener('visibilitychange', onVisibility);
    const intervalId = window.setInterval(checkIdle, CHECK_INTERVAL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, markActivity));
      document.removeEventListener('visibilitychange', onVisibility);
      window.clearInterval(intervalId);
    };
  }, [enabled, timeoutMs]);
}
